import React from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, Medal, Users, Sparkles, ArrowRight } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { Reveal, Eyebrow, PageHero, BtnPrimary, BtnGhost, CtaBand, Crumbs, FaqList } from "../components/Shared";

const aboutFaqs = [
  { q: "Who owns Boston Iron Grit?", a: "Boston Iron Grit is owned and led by head coach Tina Ramos, who has coached the West Roxbury community since the gym affiliated with CrossFit in 2011 as affiliate #1360." },
  { q: "Is Boston Iron Grit the same gym as CrossFit Boston Iron & Grit?", a: "Yes — same owner, same coaches, same location at 1230 VFW Parkway. We rebranded from CrossFit Boston Iron & Grit to Boston Iron Grit; we remain CrossFit affiliate #1360." },
  { q: "What are the coaches' credentials?", a: "Our coaching staff holds CrossFit Level 2 (CF-L2) certifications along with specialty credentials in Olympic weightlifting, nutrition coaching and adaptive training — and every coach trains on the same floor as the members." },
  { q: "How long has the gym been open?", a: "Since 2011 — over fifteen years of coaching in the same West Roxbury neighborhood, making us one of the longest-running CrossFit gyms in Boston." },
  { q: "Do you offer adaptive training?", a: "Yes. Coach Cassandra Eddy-Stewart is Boston Iron Grit's Adaptive Coach Specialist — an adaptive athlete herself, she works with athletes training around injury, disability or limited mobility at our West Roxbury gym." },
];
import { COACHES, IMG, SITE, PROOF } from "../data/mock";

const values = [
  { icon: Users, title: "No-Judgement Community", text: "All shapes, sizes, ages and abilities. Members call this gym their second home — some call it their church." },
  { icon: Medal, title: "Coaching That Scales", text: "Every workout meets you where you are. Beginners and 15-year athletes train side by side, each with their own version." },
  { icon: HeartHandshake, title: "Give Back", text: "Since 2011 we've volunteered for homeless children and Boston's most impoverished neighborhoods. Fit people give more." },
  { icon: Sparkles, title: "Live B.I.G. 365", text: "Boston Iron Grit isn't a 6-week challenge. It's a 365-day-a-year way of moving through life — stronger, happier, together." },
];

export default function About() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
  ];

  return (
    <>
      <Seo
        title="About Boston Iron Grit | West Roxbury Gym Since 2011"
        description="Boston Iron Grit (formerly CrossFit Boston Iron & Grit) has coached West Roxbury since 2011. Meet the coaches and see what makes this gym different."
        path="/about"
        schemas={[
          breadcrumbSchema(crumbs),
          ...COACHES.map((c) => ({
            "@context": "https://schema.org",
            "@type": "Person",
            "@id": `https://bostonirongrittraining.com/about#${c.name.toLowerCase().replace(/\s+/g, "-")}`,
            name: `Coach ${c.name}`,
            jobTitle: c.role,
            worksFor: { "@id": "https://bostonirongrittraining.com/#business" },
            description: c.bio,
          })),
          faqSchema(aboutFaqs),
        ]}
      />
      <PageHero
        label="Our Story"
        title={<>Fifteen years of grit.<br /><span className="text-neutral-500">One neighborhood.</span></>}
        sub="We affiliated in 2011 with a simple mission: create a community of fit, healthy and happy people who live their best life — and by extension, help the youth of Boston."
        image={IMG.interior}
        video="/video/hero-extended-30s"
      />

      <section className="bg-[#0A0A0B] pb-6">
        <div className="max-w-7xl mx-auto px-6">
          <Crumbs items={crumbs} />
        </div>
      </section>

      {/* Story + rebrand note */}
      <section className="bg-[#0A0A0B] py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>Est. 2011 · Affiliate #1360</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight">
              Same iron.<br />Same grit.<br /><span className="text-[#9D0423]">Sharper name.</span>
            </h2>
            <p className="mt-6 text-neutral-300 text-base leading-relaxed">
              You may have known us as CrossFit Boston Iron & Grit. We're now <strong className="text-white">Boston Iron Grit</strong> —
              because what we build here goes beyond any one methodology. CrossFit classes are still the heartbeat of the
              gym, now joined by HYROX training, the Westie Barbell Club, B.I.G. Engine conditioning and the Live B.I.G. 365 longevity program.
            </p>
            <p className="mt-4 text-neutral-400 text-base leading-relaxed">
              Over 15 years, the Iron Grit team and community have volunteered to help homeless children and the kids in
              Boston's most impoverished neighborhoods — while keeping ourselves strong enough to keep showing up.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 border border-white/5">
              {PROOF.map((p) => (
                <div key={p.label} className="bg-[#0D0D0F] p-5">
                  <div className="font-heading font-extrabold text-2xl text-white">{p.value}</div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-neutral-500 mt-1">{p.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMG.group} alt="Members training together at Boston Iron Grit" className="w-full h-72 object-cover object-[center_25%]" loading="lazy" />
              <img src={IMG.coach} alt="Coach guiding an athlete through a movement" className="w-full h-72 object-cover object-[center_25%] mt-10" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>What We Stand For</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight mb-12">
              The Iron Grit way
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 80}>
                <div className="p-7 bg-[#121214] border border-white/5 hover:border-[#9D0423]/40 transition-colors h-full">
                  <v.icon size={22} className="text-[#9D0423]" />
                  <h3 className="mt-5 font-heading font-bold uppercase text-lg text-white tracking-tight">{v.title}</h3>
                  <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Coaches */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>The Coaches</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-4xl lg:text-5xl text-white leading-[0.95] tracking-tight mb-12">
              The people in<br /><span className="text-neutral-600">your corner.</span>
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {COACHES.map((c, i) => (
              <Reveal key={c.name} delay={i * 80}>
                <div className="group bg-[#121214] border border-white/5 hover:border-white/15 transition-colors h-full" data-testid={`coach-card-${c.name.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="h-1 bg-[#9D0423] w-0 group-hover:w-full transition-all duration-500" />
                  {c.photo ? (
                    <div className="relative aspect-[3/2] overflow-hidden">
                      <img
                        src={c.photo}
                        alt={`Coach ${c.name} at Boston Iron Grit`}
                        className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#121214] via-transparent to-transparent opacity-60" />
                    </div>
                  ) : null}
                  <div className="p-7">
                    {!c.photo && (
                      <div className="w-14 h-14 mb-5 bg-[#9D0423]/12 border border-[#9D0423]/30 flex items-center justify-center font-heading font-extrabold text-2xl text-[#E56A79]">
                        {c.name.charAt(0)}
                      </div>
                    )}
                    <h3 className="font-heading font-bold uppercase text-xl text-white tracking-tight">Coach {c.name}</h3>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#E56A79] mt-1.5">{c.role}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500 mt-1">{c.creds}</p>
                    <p className="mt-4 text-[13px] text-neutral-400 leading-relaxed">{c.bio}</p>
                    <Link
                      to={`/coaches/${c.slug}`}
                      className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-[#E56A79] hover:text-white transition-colors"
                      data-testid={`coach-bio-link-${c.slug}`}
                    >
                      Full Bio <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={100}>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <BtnPrimary to="/get-started">Train With Us</BtnPrimary>
              <BtnGhost href={SITE.social.crossfit}>Official CrossFit Affiliate Listing</BtnGhost>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#0A0A0B] py-16 lg:py-20" data-testid="about-faq">
        <div className="max-w-4xl mx-auto px-6">
          <FaqList faqs={aboutFaqs} title="About the Gym — FAQs" />
        </div>
      </section>

      <CtaBand />
    </>
  );
}
